import { UserProfile } from '../datatypes/user-profile';
import { ApiFactory } from '../api/easypos-api';
import { SimpleDialog } from '../components/dialog';
import { write } from '../language/lang';
import { IFacebookAuthenticationResponse } from '../model/facebook.js';

import { Awaitable } from '../utility/awaitable-data';
import { writeMdTemplate } from '../utility/markdown';

export class FacebookAuth {
  private static setUserProfile?: (profile: UserProfile | undefined) => void;
  static initFacebookSDK(
    setUserProfile?: (profile: UserProfile | undefined) => void
  ) {
    FacebookAuth.setUserProfile = setUserProfile;
    // Load Facebook SDK
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    (window as unknown as { fbAsyncInit?: () => void }).fbAsyncInit = () => {
      FB.init({
        appId: '1735779930323320', // Replace with your Facebook App ID
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
      //we are not checking for login status because we dont stay connected to facebook.
    };
  }

  static async showLoginFailedDialog(content: Awaitable) {
    const dialog = SimpleDialog.build({
      header: write`${'ref:dialog:facebook-login-failed:title'}Login Error`,
      content: content,
    });

    await (
      await dialog.run()
    ).onClose;
  }

  static logoutFromFacebook() {
    FB.logout((response: unknown) => {
      console.log('Logged out from Facebook:', response);
    });
  }

  static disconnectFromFacebook() {
    FB.logout((response: unknown) => {
      console.log('disconnected from Facebook:', response);
    });
  }

  static async loginWithFacebook() {
    if (!FacebookAuth.setUserProfile) {
      throw new Error('setUserProfile is not assigned');
    }

    try {
      const response = (await new Promise((resolve) => {
        FB.login(resolve, { scope: 'public_profile,email' });
      })) as IFacebookAuthenticationResponse;

      if (response.status === 'connected') {
        const api = ApiFactory.anonymous();
        let res = await api.loginWithFacebook(
          response.authResponse.accessToken
        );

        if (!res.success || !res.data) {
          FacebookAuth.logoutFromFacebook();
          await FacebookAuth.showLoginFailedDialog(
            writeMdTemplate`${'ref:dialog:facebook-login-failed:body'}
            # Status
            connection to facebook successful but login failed.
            Reason: ${res.error}`
          );
          return;
        }
        let base64data = (await api.myProfilePicture()).data ?? undefined;
        const easyposToken = res.data;
        const userProfile = new UserProfile({
          id: easyposToken.id,
          displayName: easyposToken.displayName,
          emailAddress: easyposToken.emailAddress ?? undefined,
          authProvider: 'facebook',
          profileImageBase64: base64data,
          accessToken: easyposToken,
        });

        // Dispatch the same window event the email-login path uses so
        // app-index's _onUserProfileSet handler runs the full post-login flow
        // (persist, restore active shop, route off the login page, connect
        // realtime, join the shop group). Calling setUserProfile directly
        // here would silently leave the user on the login page.
        window.dispatchEvent(
          new CustomEvent<UserProfile>('user-profile-set', {
            detail: userProfile,
          })
        );
        //we dont keep facebook context.
        FacebookAuth.disconnectFromFacebook();
      }
    } catch (error) {
      console.error('Facebook login error:', error);
    }
  }
}
